import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCart } from '../../context/CartContext';
import { createCheckoutIntent, serializeCartForCheckout, submitCheckoutOrder } from '../../utils/checkout';

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const CheckoutForm = ({
  customer,
  onCustomerChange,
  onPaymentSuccess,
  disabled,
  statusMessage,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    onCustomerChange((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setLocalError('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: customer.fullName,
              email: customer.email,
              phone: customer.phone,
              address: {
                line1: customer.address,
                city: customer.city,
                state: customer.state,
                postal_code: customer.zip,
              },
            },
          },
          receipt_email: customer.email,
        },
        redirect: 'if_required',
      });

      if (error) {
        setLocalError(error.message || 'No pudimos procesar tu pago.');
        setSubmitting(false);
        return;
      }

      await onPaymentSuccess(paymentIntent);
      setSubmitting(false);
    } catch (error) {
      setLocalError(error.message || 'Ocurrió un error inesperado al confirmar el pago.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Nombre completo
          <input
            type="text"
            name="fullName"
            value={customer.fullName}
            onChange={handleChange}
            required
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Email
          <input
            type="email"
            name="email"
            value={customer.email}
            onChange={handleChange}
            required
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Teléfono / WhatsApp
          <input
            type="tel"
            name="phone"
            value={customer.phone}
            onChange={handleChange}
            required
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Ubicación del proyecto
          <input
            type="text"
            name="projectLocation"
            value={customer.projectLocation}
            onChange={handleChange}
            placeholder="Ciudad o urbanización"
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Dirección
          <input
            type="text"
            name="address"
            value={customer.address}
            onChange={handleChange}
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Ciudad
          <input
            type="text"
            name="city"
            value={customer.city}
            onChange={handleChange}
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Estado / Región
          <input
            type="text"
            name="state"
            value={customer.state}
            onChange={handleChange}
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
        <label className="flex flex-col text-sm font-semibold text-gray-700">
          Código postal
          <input
            type="text"
            name="zip"
            value={customer.zip}
            onChange={handleChange}
            className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </label>
      </div>

      <label className="flex flex-col text-sm font-semibold text-gray-700">
        Notas para el asesor
        <textarea
          name="notes"
          value={customer.notes}
          onChange={handleChange}
          rows="3"
          placeholder="Medidas adicionales, acabados específicos, urgencia del proyecto..."
          className="mt-1 rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 focus:border-emerald-500 focus:ring-emerald-500"
        />
      </label>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {localError && <p className="text-sm text-red-500">{localError}</p>}
      {statusMessage && <p className="text-sm text-emerald-600">{statusMessage}</p>}

      <button
        type="submit"
        disabled={disabled || submitting || !stripe || !elements}
        className="w-full rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Procesando pago...' : 'Pagar y generar orden'}
      </button>
    </form>
  );
};

const CheckoutPage = () => {
  const { items, subtotal, taxes, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({
    fullName: '',
    email: '',
    phone: '',
    projectLocation: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
  });
  const [clientSecret, setClientSecret] = useState('');
  const [intentId, setIntentId] = useState('');
  const [status, setStatus] = useState({ loading: false, error: '', success: '' });
  const [orderResult, setOrderResult] = useState(null);
  const [leadReference, setLeadReference] = useState('');
  const [savingLead, setSavingLead] = useState(false);

  const cartSnapshot = useMemo(() => serializeCartForCheckout(items), [items]);

  useEffect(() => {
    if (!items.length && !status.success) {
      navigate('/ecommerce');
    }
  }, [items, status.success, navigate]);

  useEffect(() => {
    if (!items.length || !stripePromise) return;
    let ignore = false;
    const payload = {
      items: cartSnapshot,
      subtotal,
      taxes,
      total,
    };
    setStatus((prev) => ({ ...prev, loading: true, error: '' }));
    createCheckoutIntent(payload)
      .then((data) => {
        if (ignore) return;
        setClientSecret(data.clientSecret || '');
        setIntentId(data.intentId || data.id || '');
        setLeadReference(data.leadId || data.reference || '');
        setStatus((prev) => ({ ...prev, loading: false }));
      })
      .catch((error) => {
        if (ignore) return;
        setStatus({
          loading: false,
          error: error.message || 'No pudimos preparar el checkout.',
          success: '',
        });
      });
    return () => {
      ignore = true;
    };
  }, [items.length, cartSnapshot, subtotal, taxes, total]);

  const handlePaymentSuccess = async (paymentIntent) => {
    const payload = {
      paymentIntentId: paymentIntent?.id || intentId,
      customer,
      cart: cartSnapshot,
      totals: { subtotal, taxes, total },
      leadReference,
    };
    setSavingLead(true);
    try {
      const result = await submitCheckoutOrder(payload);
      clearCart();
      setOrderResult(result);
      setStatus({
        loading: false,
        error: '',
        success:
          'Pago confirmado. Un asesor coordinará la instalación y recibirás un resumen en tu email.',
      });
    } catch (error) {
      setStatus({
        loading: false,
        error:
          'Pago aprobado, pero no registramos la orden automáticamente. Nuestro equipo la documentará manualmente.',
        success: '',
      });
    } finally {
      setSavingLead(false);
    }
  };

  const renderOrderItems = () => (
    <ul className="space-y-4">
      {cartSnapshot.map((item) => (
        <li key={item.productId + item.name} className="rounded-2xl border border-gray-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{item.name}</p>
              <p className="text-xs text-gray-500">Base: {formatCurrency(item.basePrice)}</p>
              {item.variants?.length > 0 && (
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  {item.variants.map((variant) => (
                    <p key={`${item.productId}-${variant.attribute}`}>
                      <span className="font-semibold">{variant.attribute}:</span> {variant.value}{' '}
                      {variant.priceAdjustment
                        ? `(${variant.priceAdjustment > 0 ? '+' : ''}${formatCurrency(variant.priceAdjustment)})`
                        : ''}
                    </p>
                  ))}
                </div>
              )}
              {item.addons?.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  <p className="font-semibold">Add-ons:</p>
                  <ul className="ml-3 list-disc">
                    {item.addons.map((addon) => (
                      <li key={`${item.productId}-${addon.name}`}>
                        {(addon.categoryName ? `${addon.categoryName} · ` : '') + addon.name}
                        {addon.quantity ? ` x${addon.quantity}` : ''}
                        {addon.price
                          ? ` (${formatCurrency(addon.price)}${addon.quantity ? ' c/u' : ''})`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <p className="text-sm font-semibold text-emerald-700">{formatCurrency(item.totalPrice)}</p>
          </div>
        </li>
      ))}
    </ul>
  );

  if (!stripePromise) {
    return (
      <section className="min-h-screen bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-10 shadow-xl">
          <p className="text-sm uppercase tracking-[0.4em] text-gray-500 mb-3">Checkout</p>
          <h1 className="text-3xl font-bold text-gray-900">Configura las llaves de Stripe</h1>
          <p className="mt-3 text-sm text-gray-600">
            Agrega <code className="rounded bg-gray-100 px-1 py-0.5">REACT_APP_STRIPE_PUBLIC_KEY</code> en tu entorno
            para habilitar el cobro con tarjetas.
          </p>
          <Link
            to="/ecommerce"
            className="mt-6 inline-flex rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
          >
            Regresar a la tienda
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700">Puerto Rico Windows & Doors</p>
            <h1 className="text-3xl font-bold text-gray-900">Checkout y pago seguro</h1>
            <p className="text-sm text-gray-600">
              Revisa tu carrito, ingresa tus datos y realiza el pago seguro para reservar tu orden. Confirmamos precios
              finales luego de validar medidas y certificaciones.
            </p>
          </div>
          <Link
            to="/ecommerce"
            className="inline-flex items-center justify-center rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
          >
            Seguir comprando
          </Link>
        </div>

        {status.error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {status.error}
          </div>
        )}
        {status.success && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 space-y-2">
            <p>{status.success}</p>
            {orderResult?.warnings?.length > 0 && (
              <p className="text-xs text-amber-700">
                {orderResult.warnings.join(' ')} Nuestro equipo verificará manualmente.
              </p>
            )}
            {orderResult?.pdfUrl && (
              <a
                href={orderResult.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex text-xs font-semibold text-emerald-800 underline"
              >
                Descargar resumen PDF
              </a>
            )}
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
          <div className="rounded-3xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos del proyecto y pago</h2>
            {clientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: { theme: 'flat', variables: { colorPrimary: '#047857' } },
                }}
                key={clientSecret}
              >
                <CheckoutForm
                  customer={customer}
                  onCustomerChange={setCustomer}
                  onPaymentSuccess={handlePaymentSuccess}
                  disabled={savingLead || Boolean(status.success)}
                  statusMessage={status.loading ? 'Preparando el pago...' : ''}
                />
              </Elements>
            ) : (
              <p className="text-sm text-gray-500">Preparando el formulario seguro...</p>
            )}
          </div>

          <aside className="rounded-3xl bg-white p-6 shadow-xl">
            <div className="border-b border-gray-100 pb-4">
              <h3 className="text-lg font-semibold text-gray-900">Resumen del pedido</h3>
              <p className="text-xs uppercase tracking-[0.4em] text-gray-500">
                {cartSnapshot.length} productos
              </p>
              {orderResult?.orderId && (
                <p className="mt-1 text-xs font-semibold text-emerald-700">Orden #{orderResult.orderId}</p>
              )}
            </div>
            <div className="mt-4 space-y-4">{renderOrderItems()}</div>
            <div className="mt-6 space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Impuestos</span>
                <span>{formatCurrency(taxes)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold text-gray-900">
                <span>Total estimado</span>
                <span>{formatCurrency(total)}</span>
              </div>
              <p className="text-xs text-gray-500">
                Tarifas sujetas a validación de medidas y certificaciones solicitadas.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default CheckoutPage;
